import axios from 'axios';
import { useCallback, useContext } from 'react';
import { AppContext } from '../utils/AppContext';

const useAuthentication = () => {
  const { customer, validateSessionWithJWT, clientId, activeConfigurationsURL, referrerURL } =
    useContext(AppContext);
  const validateSessionWithJWTEnabled = validateSessionWithJWT.enabled;
  const login = useCallback(
    async (email?: string, password?: string, captcha?: string) => {
      if (activeConfigurationsURL) {
        const host = process.env.REACT_APP_AUTH_SERVICE_URI || activeConfigurationsURL!.origin;
        if (validateSessionWithJWTEnabled) {
          try {
            const res = await axios.post(
              `${host}/client/basic/login`,
              {
                clientId,
                email,
                password,
                ...(captcha && { captcha }),
              },
              {
                headers: {
                  ...(customer && { customer }),
                },
              }
            );

            return {
              token: res.data?.accessToken,
              requirePasswordReset: res.data?.requirePasswordReset,
            };
          } catch (error) {
            console.log('debug log', error);
          }
        }

        return axios
          .post(
            `${activeConfigurationsURL!.origin}/auth`,
            {
              identification: email,
              password,
              ...(captcha && { captcha }),
            },
            {
              headers: {
                ...(customer && { customer }),
              },
            }
          )
          .then((res) => ({
            token: res.data?.token,
            requirePasswordReset: res.data?.requirePasswordReset,
          }));
      }
      return Promise.reject();
    },
    [activeConfigurationsURL, customer, validateSessionWithJWTEnabled, clientId]
  );

  const forgotPasswordEmail = useCallback(
    (email: string, captcha?: string) => {
      if (activeConfigurationsURL) {
        const host = process.env.REACT_APP_AUTH_SERVICE_URI || activeConfigurationsURL!.origin;
        if (validateSessionWithJWTEnabled) {
          return axios.post(`${host}/client/basic/forgot`, {
            clientId,
            email,
            domain: customer,
          });
        }
        return axios.post(
          `${activeConfigurationsURL.origin}/public_api/v1/users/${email}/resetPassword`,
          {
            captcha,
          },
          {
            headers: {
              ...(customer && { customer }),
            },
          }
        );
      }
      return Promise.reject();
    },
    [activeConfigurationsURL, customer, validateSessionWithJWTEnabled]
  );

  const resetPassword = useCallback(
    ({
      tokenFromForgotPasswordEmail,
      tokenFromLocalStorage,
      email,
      newPassword,
    }: {
      tokenFromForgotPasswordEmail: string | null;
      tokenFromLocalStorage?: string;
      email?: string;
      newPassword: string;
    }) => {
      if (activeConfigurationsURL) {
        if (validateSessionWithJWTEnabled) {
          const host = process.env.REACT_APP_AUTH_SERVICE_URI || activeConfigurationsURL!.origin;
          return axios.post(
            `${host}/client/basic/reset`,
            {
              tokenFromForgotPasswordEmail,
              newPassword,
            },
            {
              headers: {
                ...(customer && { customer }),
                ...(tokenFromLocalStorage && {
                  authorization: tokenFromLocalStorage,
                }),
              },
            }
          );
        }
        // If there is an email then use the force reset url,
        // otherwise use the forgot password reset flow url
        const url = email
          ? `${activeConfigurationsURL.origin}/app/users/${email}/changePassword`
          : `${activeConfigurationsURL.origin}/public_api/v1/users/${tokenFromForgotPasswordEmail}/changePassword`;

        return axios.post(
          url,
          {
            password: newPassword,
          },
          {
            headers: {
              ...(customer && { customer }),
              ...(tokenFromLocalStorage && { authorization: tokenFromLocalStorage }),
            },
          }
        );
      }
      return Promise.reject();
    },
    [activeConfigurationsURL, customer, validateSessionWithJWTEnabled]
  );

  const checkSSOEnabled = useCallback(
    (email: string, returnJWTToken?: string) => {
      if (activeConfigurationsURL && referrerURL) {
        const host = process.env.REACT_APP_AUTH_SERVICE_URI || activeConfigurationsURL.origin;
        const domain = customer || referrerURL.hostname.replace('.optibus.co', '');

        return axios.get(`${host}/client/authentication/domains/${domain}`, {
          params: {
            email,
            returnJWTToken,
            redirect_url: referrerURL!.href,
            env: window.location.hostname.includes('-')
              ? window.location.hostname.replace('-login.optibus.co', '')
              : 'titus',
          },
        });
      }
      return Promise.reject();
    },
    [activeConfigurationsURL, customer, referrerURL]
  );

  return {
    login,
    forgotPasswordEmail,
    checkSSOEnabled,
    resetPassword,
  };
};

export default useAuthentication;
